import { formatCurrencyFromMicros, formatDecimal, formatInteger, formatPercentage, formatRoas } from '../constants';

const CARD_CONFIG = [
  {
    key: 'spendMicros',
    label: 'Spend',
    format: formatCurrencyFromMicros,
    sparklineFn: (point) => toNumber(point?.spendMicros),
  },
  {
    key: 'impressions',
    label: 'Impressions',
    format: formatInteger,
    sparklineFn: (point) => toNumber(point?.impressions),
  },
  {
    key: 'clicks',
    label: 'Clicks',
    format: formatInteger,
    sparklineFn: (point) => toNumber(point?.clicks),
  },
  {
    key: 'conversions',
    label: 'Conversions',
    format: formatDecimal,
    sparklineFn: (point) => toNumber(point?.conversions),
  },
  {
    key: 'conversionValueMicros',
    label: 'Conv. Value',
    format: formatCurrencyFromMicros,
    sparklineFn: (point) => toNumber(point?.conversionValueMicros),
  },
  {
    key: 'roas',
    label: 'ROAS',
    format: formatRoas,
    sparklineFn: (point) => toNumber(point?.roas),
  },
  {
    key: 'ctr',
    label: 'CTR',
    format: formatPercentage,
    sparklineFn: (point) => {
      const impressions = toNumber(point?.impressions);

      if (impressions === 0) return 0;

      return toNumber(point?.clicks) / impressions;
    },
  },
  {
    key: 'cpcMicros',
    label: 'CPC',
    format: formatCurrencyFromMicros,
    sparklineFn: (point) => {
      const clicks = toNumber(point?.clicks);

      if (clicks === 0) return 0;

      return toNumber(point?.spendMicros) / clicks;
    },
  },
  {
    key: 'cpaMicros',
    label: 'CPA',
    format: formatCurrencyFromMicros,
    sparklineFn: (point) => {
      const conversions = toNumber(point?.conversions);

      if (conversions === 0) return 0;

      return toNumber(point?.spendMicros) / conversions;
    },
  },
];

export function SummaryCards({ summary, daily = [] }) {
  return (
    <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" aria-label="Ad performance summary">
      {CARD_CONFIG.map(({ key, label, format, sparklineFn }) => (
        <SummaryCard
          key={key}
          label={label}
          value={format(summary?.[key])}
          sparklineData={daily.map((point) => sparklineFn(point))}
        />
      ))}
    </section>
  );
}

function SummaryCard({ label, value, sparklineData }) {
  return (
    <article className="rounded-2xl border border-mystic-500 bg-white p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-lynch-500">{label}</p>
      <p className="mt-3 text-2xl font-semibold text-black-pearl-500">{value}</p>
      <Sparkline data={sparklineData} />
    </article>
  );
}

function Sparkline({ data }) {
  if (data.length < 2) {
    return null;
  }

  const width = 100;
  const height = 52;
  const verticalPadding = 6;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min;

  const points = data
    .map((value, index) => {
      const x = (index / (data.length - 1)) * width;
      const y =
        range === 0 ? height / 2 : height - verticalPadding - ((value - min) / range) * (height - verticalPadding * 2);

      return `${x},${y}`;
    })
    .join(' ');

  const areaPoints = `0,${height} ${points} ${width},${height}`;

  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${width} ${height}`}
      className="mt-4 h-[52px] w-full overflow-visible"
      preserveAspectRatio="none"
    >
      <polygon points={areaPoints} fill="rgba(0, 80, 157, 0.12)" />
      <polyline
        points={points}
        fill="none"
        stroke="#00509D"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function toNumber(value) {
  return Number(value || 0);
}
